/**
 * Service Quote Card Component
 * 서비스 견적 카드 컴포넌트
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { CheckCircle, Clock, DollarSign, MapPin } from "lucide-react";

interface ServiceQuote {
  id: number;
  business_id: string;
  business_name?: string;
  quote_amount: number;
  description: string | null;
  estimated_date?: string | null;
  estimated_duration?: string | null;
  valid_until?: string | null;
  status: "pending" | "accepted" | "rejected" | "expired";
  created_at?: string;
}

interface ServiceQuoteCardProps {
  quote: ServiceQuote;
  requestId: number;
  onAccept?: (quoteId: number) => void;
  isAccepted?: boolean;
  canAccept?: boolean;
}

export function ServiceQuoteCard({
  quote,
  requestId,
  onAccept,
  isAccepted = false,
  canAccept = true,
}: ServiceQuoteCardProps) {
  const getStatusBadge = () => {
    switch (quote.status) {
      case "pending":
        return <Badge variant="outline">대기중</Badge>;
      case "accepted":
        return <Badge className="bg-green-600">수락됨</Badge>;
      case "rejected":
        return <Badge variant="destructive">거절됨</Badge>;
      case "expired":
        return <Badge variant="secondary">만료됨</Badge>;
      default:
        return <Badge variant="secondary">{quote.status}</Badge>;
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)} AUD`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Card className={isAccepted ? "border-green-500 border-2" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold">
                {quote.business_name || `업체 ${quote.business_id.slice(0, 8)}`}
              </span>
              {getStatusBadge()}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(quote.created_at), {
                addSuffix: true,
                locale: ko,
              })}
            </p>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-5 h-5 text-green-600" />
          <span className="text-2xl font-bold text-green-600">
            {formatPrice(quote.quote_amount)}
          </span>
        </div>

        {/* Description */}
        {quote.description && (
          <div className="mb-3">
            <p className="text-sm text-muted-foreground">{quote.description}</p>
          </div>
        )}

        {/* Details */}
        <div className="space-y-2 text-sm mb-3">
          {quote.estimated_date && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                예상 일정: {formatDate(quote.estimated_date)}
              </span>
            </div>
          )}
          {quote.estimated_duration && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                예상 소요 시간: {quote.estimated_duration}
              </span>
            </div>
          )}
          {quote.valid_until && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                견적 유효기간: {formatDate(quote.valid_until)}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        {quote.status === "pending" && canAccept && !isAccepted && (
          <Button
            className="w-full"
            onClick={() => onAccept?.(quote.id)}
            variant="default"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            이 견적 수락하기
          </Button>
        )}

        {isAccepted && (
          <div className="flex items-center justify-center gap-2 text-green-600 font-semibold">
            <CheckCircle className="w-5 h-5" />
            <span>수락된 견적</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

